import type {
    DiskAncestor,
    DiskCollection,
    DiskFileKind,
    DiskFileVersion,
    DiskHistoryItem,
    DiskNode,
    DiskPermissions,
    DiskQuota,
    DiskQuotaLine,
    DiskRole,
    DiskStorage,
    DiskStorageRef,
    DiskStorageType,
    DiskUserRef,
} from '../Contracts/DiskNode'
import type {
    DiskDirectShare,
    DiskInheritedShare,
    DiskMyAccess,
    DiskNodeRef,
    DiskNodeShares,
    DiskPrincipalOption,
    DiskPrincipalRow,
    DiskPrincipalType,
    DiskRoleRef,
    DiskSharedInfo,
    DiskShareRow,
    DiskShareSummary,
} from '../Contracts/DiskShare'
import type { DiskUploadSession } from '../Contracts/DiskUpload'

/**
 * Розбір відповідей API диска.
 *
 * Ядровий `restClient` типізований як `KyResponse<unknown>`, хоча віддає
 * розібраний JSON (`development.md`, строгі прапорці), тож кожне поле зводимо
 * явно: зіпсоване значення стає `null`/нулем, а не `undefined` у розмітці.
 */
type Raw = Record<string, unknown>

const asRecord = (value: unknown): Raw => (value !== null && typeof value === 'object' ? (value as Raw) : {})
const str = (value: unknown): string | null => (typeof value === 'string' ? value : null)
const num = (value: unknown): number => (typeof value === 'number' && Number.isFinite(value) ? value : 0)
const numOrNull = (value: unknown): number | null =>
    typeof value === 'number' && Number.isFinite(value) ? value : null

const STORAGE_TYPES: readonly DiskStorageType[] = ['personal', 'company', 'department', 'custom']
const ROLES: readonly DiskRole[] = ['viewer', 'commenter', 'editor', 'manager']
const KINDS: readonly DiskFileKind[] = [
    'folder',
    'image',
    'pdf',
    'video',
    'audio',
    'text',
    'document',
    'spreadsheet',
    'presentation',
    'archive',
    'other',
]

const storageType = (value: unknown): DiskStorageType => STORAGE_TYPES.find((type) => type === value) ?? 'custom'

const role = (value: unknown): DiskRole | null => ROLES.find((item) => item === value) ?? null

export const toUser = (value: unknown): DiskUserRef | null => {
    if (value === null || typeof value !== 'object') return null

    const raw = asRecord(value)
    const user: DiskUserRef = {}
    const uuid = str(raw['uuid'])
    const shortName = str(raw['shortName'])
    const fullName = str(raw['fullName'])
    const firstName = str(raw['firstName'])
    const lastName = str(raw['lastName'])
    const photoUrl = str(raw['photoUrl'])

    if (uuid !== null) user.uuid = uuid
    if (shortName !== null) user.shortName = shortName
    if (fullName !== null) user.fullName = fullName
    if (firstName !== null) user.firstName = firstName
    if (lastName !== null) user.lastName = lastName
    if (photoUrl !== null && photoUrl !== '') user.photoUrl = photoUrl

    return user
}

const toPermissions = (value: unknown): DiskPermissions => {
    const raw = asRecord(value)
    const permissions: DiskPermissions = {}

    for (const key of ['read', 'comment', 'write', 'update', 'delete', 'share', 'manage'] as const) {
        if (raw[key] === true) permissions[key] = true
    }

    return permissions
}

const toStorageRef = (value: unknown): DiskStorageRef | null => {
    const raw = asRecord(value)
    const uuid = str(raw['uuid'])

    return uuid === null ? null : { uuid, type: storageType(raw['type']), title: str(raw['title']) ?? '' }
}

const toAncestors = (value: unknown): DiskAncestor[] | null =>
    Array.isArray(value)
        ? value.map((item) => {
              const raw = asRecord(item)

              return { uuid: str(raw['uuid']) ?? '', name: str(raw['name']) ?? '', root: raw['root'] === true }
          })
        : null

const toVersion = (value: unknown): DiskFileVersion | null => {
    if (value === null || typeof value !== 'object') return null

    const raw = asRecord(value)

    return {
        uuid: str(raw['uuid']) ?? '',
        number: num(raw['number']),
        size: num(raw['size']),
        mime: str(raw['mime']),
        createdAt: str(raw['createdAt']),
    }
}

const toShareSummary = (value: unknown): DiskShareSummary | null => {
    if (value === null || typeof value !== 'object') return null

    const raw = asRecord(value)

    return { people: num(raw['people']), departments: num(raw['departments']), roles: num(raw['roles']), company: raw['company'] === true }
}

const toSharedInfo = (value: unknown): DiskSharedInfo | null => {
    if (value === null || typeof value !== 'object') return null

    const raw = asRecord(value)

    return { shareUuid: str(raw['shareUuid']) ?? '', sharedAt: str(raw['sharedAt']), sharedBy: toUser(raw['sharedBy']) }
}

export const toNode = (value: unknown): DiskNode => {
    const raw = asRecord(value)
    const type = raw['type'] === 'folder' ? 'folder' : 'file'

    return {
        uuid: str(raw['uuid']) ?? '',
        type,
        name: str(raw['name']) ?? '',
        size: num(raw['size']),
        mime: str(raw['mime']),
        kind: KINDS.find((kind) => kind === raw['kind']) ?? (type === 'folder' ? 'folder' : 'other'),
        storage: toStorageRef(raw['storage']),
        parentUuid: str(raw['parentUuid']),
        createdAt: str(raw['createdAt']),
        createdBy: toUser(raw['createdBy']),
        updatedAt: str(raw['updatedAt']),
        updatedBy: toUser(raw['updatedBy']),
        role: role(raw['role']),
        ancestors: toAncestors(raw['ancestors']),
        currentVersion: toVersion(raw['currentVersion']),
        deletedAt: str(raw['deletedAt']),
        deletedBy: toUser(raw['deletedBy']),
        originalParentUuid: str(raw['originalParentUuid']),
        originalParentName: str(raw['originalParentName']),
        shareSummary: toShareSummary(raw['shareSummary']),
        sharedWithMe: raw['sharedWithMe'] === true,
        shared: toSharedInfo(raw['shared']),
        permissions: toPermissions(raw['_permissions']),
    }
}

export const toCollection = <T>(value: unknown, map: (item: unknown) => T): DiskCollection<T> => {
    const raw = asRecord(value)
    const items = Array.isArray(raw['items']) ? raw['items'].map(map) : []
    const meta = asRecord(raw['meta'])

    return { items, total: typeof meta['total'] === 'number' ? meta['total'] : items.length }
}

export const toStorage = (value: unknown): DiskStorage => {
    const raw = asRecord(value)
    const settings = raw['settings'] !== null && typeof raw['settings'] === 'object' ? asRecord(raw['settings']) : null

    return {
        uuid: str(raw['uuid']) ?? '',
        type: storageType(raw['type']),
        title: str(raw['title']) ?? '',
        role: role(raw['role']),
        rootFolderUuid: str(raw['rootFolderUuid']) ?? '',
        usedBytes: num(raw['usedBytes']),
        effectiveQuotaBytes: numOrNull(raw['effectiveQuotaBytes']),
        settings:
            settings === null
                ? null
                : {
                      editorsCanShare: settings['editorsCanShare'] === true,
                      versionsToKeep: num(settings['versionsToKeep']),
                      trashDays: num(settings['trashDays']),
                  },
    }
}

const toQuotaLine = (value: unknown): DiskQuotaLine => {
    const raw = asRecord(value)

    return {
        uuid: str(raw['uuid']),
        title: str(raw['title']) ?? '',
        type: raw['type'] === undefined || raw['type'] === null ? null : storageType(raw['type']),
        quotaBytes: numOrNull(raw['quotaBytes']),
        usedBytes: num(raw['usedBytes']),
        reservedBytes: num(raw['reservedBytes']),
        percent: numOrNull(raw['percent']),
    }
}

export const toQuota = (value: unknown): DiskQuota => {
    const raw = asRecord(value)

    return {
        personal: raw['personal'] !== null && typeof raw['personal'] === 'object' ? toQuotaLine(raw['personal']) : null,
        managed: Array.isArray(raw['managed']) ? raw['managed'].map(toQuotaLine) : [],
    }
}

export const toHistoryItem = (value: unknown): DiskHistoryItem => {
    const raw = asRecord(value)

    return {
        uuid: str(raw['uuid']) ?? '',
        event: str(raw['event']) ?? '',
        payload: asRecord(raw['payload']),
        changedBy: toUser(raw['changedBy']),
        occurredAt: str(raw['occurredAt']),
    }
}

const numbers = (value: unknown): number[] =>
    Array.isArray(value) ? value.filter((item): item is number => typeof item === 'number' && Number.isInteger(item)) : []

/** Стан сесії шматків — `UploadSessionView` беку. */
export const toUploadSession = (value: unknown): DiskUploadSession => {
    const raw = asRecord(value)

    return {
        uuid: str(raw['uuid']) ?? '',
        status: str(raw['status']) ?? '',
        size: num(raw['size']),
        chunkSize: num(raw['chunkSize']),
        chunkCount: num(raw['chunkCount']),
        received: numbers(raw['received']),
        missing: numbers(raw['missing']),
        expiresAt: str(raw['expiresAt']),
    }
}

// ---- Доступи (фаза 2) --------------------------------------------------------

const PRINCIPAL_TYPES: readonly DiskPrincipalType[] = ['user', 'department', 'role', 'company']

const principalType = (value: unknown): DiskPrincipalType =>
    PRINCIPAL_TYPES.find((type) => type === value) ?? 'company'

const toNodeRef = (value: unknown): DiskNodeRef | null => {
    if (value === null || typeof value !== 'object') return null

    const raw = asRecord(value)
    const uuid = str(raw['uuid'])

    return uuid === null ? null : { uuid, name: str(raw['name']) ?? '' }
}

const toRoleRef = (value: unknown): DiskRoleRef | null => {
    const raw = asRecord(value)
    const parsed = role(raw['role'])

    return parsed === null ? null : { role: parsed, roleLabel: str(raw['roleLabel']) ?? '' }
}

const toPrincipalRow = (value: unknown): DiskPrincipalRow => {
    const raw = asRecord(value)
    const principal = asRecord(raw['principal'])

    return {
        uuid: str(raw['uuid']) ?? '',
        principalType: principalType(raw['principalType']),
        principal: { uuid: str(principal['uuid']), code: str(principal['code']) },
        principalKey: str(raw['principalKey']) ?? '',
        principalTitle: str(raw['principalTitle']) ?? '',
        principalUser: toUser(raw['principalUser']),
        role: role(raw['role']) ?? 'viewer',
        roleLabel: str(raw['roleLabel']) ?? '',
    }
}

/** Рядок доступу — і відповідь `POST`/`PATCH`/`DELETE` доступу. */
export const toShareRow = (value: unknown): DiskShareRow => {
    const raw = asRecord(value)

    return {
        ...toPrincipalRow(value),
        expiresAt: str(raw['expiresAt']),
        grantedAt: str(raw['grantedAt']),
        updatedAt: str(raw['updatedAt']),
        grantedBy: toUser(raw['grantedBy']),
    }
}

const toInherited = (value: unknown): DiskInheritedShare => ({
    ...toShareRow(value),
    fromNode: toNodeRef(asRecord(value)['fromNode']),
})

const toDirect = (value: unknown): DiskDirectShare => {
    const raw = asRecord(value)

    return {
        ...toShareRow(value),
        inheritedRole: raw['inheritedRole'] ? toRoleRef(raw['inheritedRole']) : null,
        effectiveRole: raw['effectiveRole'] ? toRoleRef(raw['effectiveRole']) : null,
    }
}

const ACCESS_SOURCES = ['owner', 'admin', 'storage', 'share'] as const

const toMyAccess = (value: unknown): DiskMyAccess => {
    const raw = asRecord(value)
    const source = raw['source'] !== null && typeof raw['source'] === 'object' ? asRecord(raw['source']) : null
    const sourceType = ACCESS_SOURCES.find((type) => type === source?.['type'])

    return {
        role: role(raw['role']),
        roleLabel: str(raw['roleLabel']),
        canShare: raw['canShare'] === true,
        maxGrantRole: role(raw['maxGrantRole']),
        source:
            source !== null && sourceType !== undefined
                ? { type: sourceType, title: str(source['title']) ?? '', node: toNodeRef(source['node']) }
                : null,
    }
}

const list = <T>(value: unknown, map: (item: unknown) => T): T[] => (Array.isArray(value) ? value.map(map) : [])

export const toNodeShares = (value: unknown): DiskNodeShares => {
    const raw = asRecord(value)
    const node = asRecord(raw['node'])
    const storage = asRecord(raw['storage'])

    return {
        node: {
            uuid: str(node['uuid']) ?? '',
            name: str(node['name']) ?? '',
            type: node['type'] === 'folder' ? 'folder' : 'file',
            root: node['root'] === true,
        },
        storage: {
            uuid: str(storage['uuid']) ?? '',
            title: str(storage['title']) ?? '',
            type: storageType(storage['type']),
            editorsCanShare: storage['editorsCanShare'] === true,
        },
        owner: toUser(raw['owner']),
        me: toMyAccess(raw['me']),
        storageMembers: list(raw['storageMembers'], toPrincipalRow),
        inherited: list(raw['inherited'], toInherited),
        direct: list(raw['direct'], toDirect),
    }
}

export const toPrincipalOption = (value: unknown): DiskPrincipalOption => {
    const raw = asRecord(value)

    return {
        principalType: principalType(raw['principalType']),
        principal: str(raw['principal']),
        principalKey: str(raw['principalKey']) ?? '',
        title: str(raw['title']) ?? '',
        subtitle: str(raw['subtitle']) ?? '',
        user: toUser(raw['user']),
    }
}
